import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSignOutAlt,
    faHouse,
    faBookOpen,
    faSliders,
    faBuildingColumns,
    faLayerGroup,
    faChevronRight,
    faChevronLeft,
    faSun,
    faMoon,
    faGear,
    faCommentDots
} from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import { handleLogout } from "../../utils/custom";
import { useNavigate } from 'react-router-dom';
import logoIcon from '../../assets/images/new_log_design.svg';
import { useLanguage } from '../../contexts/LanguageContext';
import { getFullFileUrl } from '../../Helpers/UrlHelpers';

const Sidebar = ({
    isOpen,
    onToggle,
    activeTab,
    onTabChange,
    menuItems,
    isMobile,
    searchQuery = '',
    userProfile
}) => {
    const navigate = useNavigate();
    const { t, language } = useLanguage();
    const [isDarkMode, setIsDarkMode] = useState(false);

    const normalizedMenuItems = useMemo(() => {
        let items = Array.isArray(menuItems) ? [...menuItems] : [];

        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            items = items.filter(item =>
                String(item.label || '').toLowerCase().includes(query) ||
                String(item.id || '').toLowerCase().includes(query)
            );
        }

        const hasGroupItem = items.some((item) => String(item?.id || '').toLowerCase().includes('group'));

        if (!hasGroupItem && items.length > 6) {
            items.push({
                id: '__groups_placeholder__',
                icon: faLayerGroup,
                label: t('sidebarGroups'),
            });
        }

        return items;
    }, [menuItems, searchQuery, t]);

    const groupedMenuItems = useMemo(() => {
        const safeItems = normalizedMenuItems.filter((item) => item?.id !== '__groups_placeholder__');
        const generalIds = new Set(['home', 'overview', 'dashboard', 'main']);
        const academicKeywords = ['course', 'lesson', 'material', 'exam', 'student', 'teacher', 'group'];
        const operationsKeywords = ['setting', 'attendance', 'salary', 'request', 'transaction', 'subscription'];

        const matchesKeyword = (id, keywords) => {
            const s = String(id || '').toLowerCase();
            return keywords.some((k) => s.includes(k));
        };

        const general = safeItems.filter((item) => generalIds.has(String(item?.id || '').toLowerCase()));
        const remaining = safeItems.filter((item) => !general.includes(item));

        const academic = remaining.filter((item) => matchesKeyword(item?.id, academicKeywords));
        const operations = remaining.filter(
            (item) => !academic.includes(item) && matchesKeyword(item?.id, operationsKeywords)
        );
        const management = remaining.filter((item) => !academic.includes(item) && !operations.includes(item));

        return [
            { key: 'general', label: t('sidebarGeneral') || 'OVERVIEW', items: general, icon: faHouse },
            { key: 'academic', label: t('sidebarAcademic') || 'ACADEMIC', items: academic, icon: faBookOpen },
            { key: 'operations', label: t('sidebarOperations') || 'OPERATIONS', items: operations, icon: faSliders },
            { key: 'management', label: t('sidebarPlatform') || 'MANAGEMENT', items: management, icon: faBuildingColumns },
        ].filter((group) => group.items.length > 0);
    }, [normalizedMenuItems, t]);

    const sidebarVariants = {
        open: {
            width: "260px",
            transition: { type: "spring", stiffness: 300, damping: 30 }
        },
        closed: {
            width: "80px",
            transition: { type: "spring", stiffness: 300, damping: 30 }
        }
    };

    return (
        <>
            <AnimatePresence>
                {isMobile && isOpen && (
                    <motion.div
                        className="sidebar-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 0.5 }}
                        exit={{ opacity: 0 }}
                        onClick={onToggle}
                    />
                )}
            </AnimatePresence>

            <motion.div
                className={`sidebar ${isMobile ? (isOpen ? 'open' : 'closed') : (!isOpen ? 'closed' : '')}`}
                variants={sidebarVariants}
                initial={isOpen ? "open" : "closed"}
                animate={isOpen ? "open" : "closed"}
            >
                <div className="sidebar-header">
                    <div className="brand">
                        <img src={logoIcon} alt="Logo" className="brand-icon" />
                        <AnimatePresence>
                            {isOpen && (
                                <motion.div
                                    className="brand-text"
                                    initial={{ opacity: 0, width: 0 }}
                                    animate={{ opacity: 1, width: "auto" }}
                                    exit={{ opacity: 0, width: 0 }}
                                >
                                    <span className="brand-name">{t('appName') || 'Softtech'}</span>
                                    <span className="brand-sub">Technology</span>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                    <button className="toggle-btn" onClick={onToggle}>
                        <FontAwesomeIcon icon={
                            isOpen
                                ? (language === 'ar' ? faChevronRight : faChevronLeft)
                                : (language === 'ar' ? faChevronLeft : faChevronRight)
                        } />
                    </button>
                </div>

                <nav className="sidebar-nav">
                    {groupedMenuItems.map((group) => (
                        <div className="nav-group" key={group.key}>
                            {isOpen && (
                                <div className="group-title">{group.label}</div>
                            )}
                            <div className="group-items">
                                {group.items.map((item) => (
                                    <button
                                        key={item.id}
                                        className={`nav-item ${activeTab === item.id ? 'active' : ''}`}
                                        onClick={() => onTabChange(item.id)}
                                        title={!isOpen ? item.label : undefined}
                                    >
                                        <div className="item-content">
                                            {isOpen && activeTab !== item.id && <FontAwesomeIcon icon={language === 'ar' ? faChevronLeft : faChevronRight} className="nav-chevron" />}
                                            {isOpen && activeTab === item.id && <FontAwesomeIcon icon={language === 'ar' ? faChevronLeft : faChevronRight} className="nav-chevron active-chevron" />}
                                            <FontAwesomeIcon icon={item.icon} className="nav-icon" />
                                            <AnimatePresence>
                                                {isOpen && (
                                                    <motion.span
                                                        className="nav-label"
                                                        initial={{ opacity: 0 }}
                                                        animate={{ opacity: 1 }}
                                                        exit={{ opacity: 0 }}
                                                    >
                                                        {item.label}
                                                    </motion.span>
                                                )}
                                            </AnimatePresence>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                </nav>

                <div className="sidebar-footer">
                    <div className="footer-actions">
                        <button className="docs-icon-btn" onClick={() => navigate('/teacher-documentation')} title={t('teacherDocumentation')}>
                            <FontAwesomeIcon icon={faBookOpen} />
                        </button>
                        <button className="home-icon-btn" onClick={() => navigate('/')} title={t('home') || 'Home'}>
                            <FontAwesomeIcon icon={faHouse} />
                        </button>
                        <button className="logout-icon-btn" onClick={async () => await handleLogout(navigate)} title={t('logout') || 'Log out'}>
                            <FontAwesomeIcon icon={faSignOutAlt} />
                        </button>
                    </div>

                    <div className="user-profile">
                        <img
                            src={userProfile?.profile_picture ? getFullFileUrl(userProfile.profile_picture) : "https://ui-avatars.com/api/?name=User&background=e0e7ff&color=3730a3"}
                            alt="User"
                            className="avatar"
                        />
                        <AnimatePresence>
                            {isOpen && (
                                <motion.div
                                    className="user-info"
                                    initial={{ opacity: 0, width: 0 }}
                                    animate={{ opacity: 1, width: "auto" }}
                                    exit={{ opacity: 0, width: 0 }}
                                >
                                    <span className="user-name">{userProfile?.teacher_name || userProfile?.name || 'Loading...'}</span>
                                    <span className="user-email">{userProfile?.email || ''}</span>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </motion.div>

            <style jsx>{`
                .sidebar-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(0, 0, 0, 0.4);
                    z-index: 998;
                    display: none;
                }

                .sidebar {
                    position: sticky;
                    top: 0;
                    height: 100vh;
                    background: #ffffff;
                    z-index: 1;
                    display: flex;
                    flex-direction: column;
                    flex-shrink: 0;
                    overflow-y: auto;
                    border-inline-end: 1px solid #f1f5f9;
                }

                .sidebar::-webkit-scrollbar {
                    width: 0px;
                }

                .sidebar-header {
                    padding: 24px 20px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                }

                .sidebar.closed .sidebar-header {
                    flex-direction: column;
                    padding: 24px 0;
                    gap: 24px;
                }

                .brand {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    overflow: hidden;
                }

                .brand-icon {
                    width: 36px;
                    height: 36px;
                    object-fit: contain;
                    border-radius: 8px;
                    flex-shrink: 0;
                }

                .brand-text {
                    display: flex;
                    flex-direction: column;
                    white-space: nowrap;
                }

                .brand-name {
                    font-size: 15px;
                    font-weight: 700;
                    color: #0f172a;
                    line-height: 1.2;
                }

                .brand-sub {
                    font-size: 12px;
                    color: #64748b;
                }

                .toggle-btn {
                    background: transparent;
                    border: none;
                    color: #94a3b8;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 12px;
                    padding: 4px;
                    transition: color 0.2s;
                }

                .toggle-btn:hover {
                    color: #0f172a;
                }

                .sidebar-nav {
                    padding: 0 16px;
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    gap: 24px;
                    overflow-y: auto;
                }

                .sidebar-nav::-webkit-scrollbar {
                    display: none;
                }

                .nav-group {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }

                .group-title {
                    font-size: 11px;
                    font-weight: 600;
                    color: #94a3b8;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 0 12px;
                    margin-bottom: 4px;
                }

                .group-items {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                }

                .nav-item {
                    background: transparent;
                    border: none;
                    width: 100%;
                    padding: 10px 12px;
                    border-radius: 8px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    text-align: ${language === 'ar' ? 'right' : 'left'};
                }

                .item-content {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }

                .nav-chevron {
                    font-size: 10px;
                    color: #cbd5e1;
                    width: 10px;
                }

                .active-chevron {
                    color: #6366f1;
                }   

                .nav-icon {
                    font-size: 16px;
                    color: #64748b;
                    width: 20px;
                    display: flex;
                    justify-content: center;
                }

                .nav-label {
                    font-size: 14px;
                    font-weight: 500;
                    color: #334155;
                    white-space: nowrap;
                }

                .nav-item:hover {
                    background: #f8fafc;
                }

                .nav-item.active {
                    background: #f0fdfa;
                }

                .nav-item.active .nav-icon,
                .nav-item.active .nav-label {
                    color: #6366f1;
                }

                .sidebar.closed .nav-item {
                    padding: 12px;
                    display: flex;
                    justify-content: center;
                }

                .sidebar.closed .item-content {
                    justify-content: center;
                    gap: 0;
                }

                .sidebar.closed .nav-chevron {
                    display: none;
                }

                .sidebar.closed .group-title {
                    display: none;
                }

                .sidebar-footer {
                    padding: 24px 20px;
                    border-top: 1px solid #f1f5f9;
                    display: flex;
                    flex-direction: column;
                    gap: 24px;
                }
                .footer-actions {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 8px;
                    padding: 0 12px;
                }

                .home-icon-btn,
                .docs-icon-btn {
                    background: #f1f5f9;
                    border: none;
                    color: #64748b;
                    width: 100%;
                    height: 32px;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .home-icon-btn:hover,
                .docs-icon-btn:hover {
                    background: #e2e8f0;
                    color: #334155;
                }

                .logout-icon-btn {
                    background: #fee2e2;
                    border: none;
                    color: #ef4444;
                    width: 100%;
                    height: 32px;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .logout-icon-btn:hover {
                    background: #fca5a5;
                    color: #b91c1c;
                }

                .sidebar.closed .footer-actions {
                    flex-direction: column;
                    gap: 16px;
                    padding: 0;
                }

                .theme-icon {
                    color: #94a3b8;
                    font-size: 14px;
                }

                .theme-switch {
                    width: 44px;
                    height: 24px;
                    border-radius: 12px;
                    background: #e2e8f0;
                    border: none;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    padding: 0 2px;
                    transition: background 0.3s ease;
                }

                .theme-switch.dark {
                    background: #0f172a;
                    justify-content: flex-end;
                }

                .theme-switch.light {
                    justify-content: flex-start;
                }

                .switch-knob {
                    width: 20px;
                    height: 20px;
                    background: #ffffff;
                    border-radius: 50%;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
                }

                .user-profile {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    overflow: hidden;
                }

                .avatar {
                    width: 36px;
                    height: 36px;
                    border-radius: 50%;
                    object-fit: cover;
                    flex-shrink: 0;
                }

                .user-info {
                    display: flex;
                    flex-direction: column;
                    white-space: nowrap;
                }

                .user-name {
                    font-size: 14px;
                    font-weight: 600;
                    color: #0f172a;
                }

                .user-email {
                    font-size: 12px;
                    color: #64748b;
                }
                .sidebar.closed .user-profile {
                    justify-content: center;
                }

                @media (max-width: 768px) {
                    .sidebar-overlay {
                        display: block;
                    }

                    .sidebar {
                        position: fixed;
                        z-index: 999;
                        transform: ${language === 'ar' ? 'translateX(100%)' : 'translateX(-100%)'};
                        transition: transform 0.3s ease;
                        right: ${language === 'ar' ? '0' : 'auto'};
                        left: ${language === 'ar' ? 'auto' : '0'};
                    }

                    .sidebar.open {
                        transform: translateX(0);
                    }
                }
            `}</style>
        </>
    );
};

export default Sidebar;